import React, { useState, useRef, useEffect } from "react";
import Modal from "../../components/common/modal";
import { Upload, CheckCircle, Trash2 } from "lucide-react";
import Button from "../../components/common/button";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const ProductModal = ({
  isOpen,
  onClose,
  editProduct,
  viewMode,
  mode = "create",
  onSubmit,
  isLoading = false,
}) => {
  const isDelete = mode === "delete";

  const [productName, setProductName] = useState("");
  const [productSubTitle, setProductSubTitle] = useState("");
  const [productCategory, setProductCategory] = useState("");
  const [productIndustries, setProductIndustries] = useState([]);
  const [productDescription, setProductDescription] = useState("");
  const [specs, setSpecs] = useState([{ key: "", value: "" }]);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);

  const [selectedFiles, setSelectedFiles] = useState({
    image: null,
    video: null,
    catalog: null,
  });

  const fileInputRefs = useRef({
    image: null,
    video: null,
    catalog: null,
  });

  // Seed form when editing
  useEffect(() => {
    if (isOpen && editProduct) {
      setProductName(editProduct.title || editProduct.name || "");
      setProductSubTitle(editProduct.subTitle || "");

      // Handle category
      const catVal = Array.isArray(editProduct.category)
        ? editProduct.category[0]?._id || editProduct.category[0]
        : editProduct.category?._id ||
        editProduct.category ||
        "";

      setProductCategory(catVal);

      setProductDescription(editProduct.description || "");

      // Handle tags
      const tagsList = (editProduct.tags || []).map((t) =>
        typeof t === "object"
          ? t.name || t.title || t._id
          : t
      );

      setTags(tagsList);

      // Handle industries
      const industriesList = (
        editProduct.industries || []
      ).map((i) =>
        typeof i === "object"
          ? i._id || i.id
          : i
      );

      setProductIndustries(industriesList);

      setSpecs(
        editProduct.specs?.length
          ? editProduct.specs
          : [{ key: "", value: "" }]
      );
    } else if (isOpen && !editProduct) {
      setProductName("");
      setProductSubTitle("");
      setProductCategory("");
      setProductIndustries([]);
      setProductDescription("");
      setTags([]);
      setSpecs([{ key: "", value: "" }]);

      setSelectedFiles({
        image: null,
        video: null,
        catalog: null,
      });
    }
  }, [isOpen, editProduct]);

  const openFilePicker = (fileType) => {
    fileInputRefs.current[fileType]?.click();
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files?.[0];

    if (file) {
      setSelectedFiles((prev) => ({
        ...prev,
        [fileType]: file,
      }));
    }
  };

  const clearFile = (fileType) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [fileType]: null,
    }));

    if (fileInputRefs.current[fileType]) {
      fileInputRefs.current[fileType].value = "";
    }
  };

  const addSpecification = () => {
    setSpecs([
      ...specs,
      { key: "", value: "" },
    ]);
  };

  const handleSpecChange = (
    index,
    field,
    value
  ) => {
    const updated = [...specs];
    updated[index][field] = value;
    setSpecs(updated);
  };

  const removeSpecification = (index) => {
    const updated = specs.filter(
      (_, i) => i !== index
    );

    setSpecs(
      updated.length
        ? updated
        : [{ key: "", value: "" }]
    );
  };

  const addTag = () => {
    if (!tagInput.trim()) return;

    if (!tags.includes(tagInput.trim())) {
      setTags([
        ...tags,
        tagInput.trim(),
      ]);
    }

    setTagInput("");
  };

  const removeTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const formData = {
      productName,
      productSubTitle,
      productCategory,
      productIndustries,
      productDescription,
      specs,
      tags,
      selectedFiles,
    };

    onSubmit?.(formData);
  };

  const uploadConfig = [
    {
      title: "Product Images",
      type: "image",
      accept: "image/*",
    },
    {
      title: "Product Video",
      type: "video",
      accept: "video/*",
    },
    {
      title: "Product Catalog",
      type: "catalog",
      accept: ".pdf",
    },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          isDelete
            ? "Delete Product"
            : viewMode
              ? "View Product"
              : editProduct
                ? "Edit Product"
                : "Add Product"
        }
      >
        {isDelete ? (
          <>
            <div style={{ padding: "10px 2px" }}>
              <p style={{ margin: 0, color: "#333" }}>
                Are you sure you want to delete{" "}
                <strong>{editProduct?.title || "this product"}</strong>?
              </p>
              <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>
                This action will remove it from the database and the table.
              </p>
            </div>

            <div className="modal-footer">
              <Button onClick={onClose}>Cancel</Button>
              <Button onClick={() => onSubmit?.()} disabled={isLoading}>
                {isLoading ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="form-container">
              {/* Thumbnail Preview */}
              {(editProduct?.thumbnail_image ||
                selectedFiles.image) && (
                  <div
                    style={{
                      marginBottom: "20px",
                      textAlign: "center",
                    }}
                  >
                    <img
                      src={
                        selectedFiles.image
                          ? URL.createObjectURL(
                            selectedFiles.image
                          )
                          : resolveImageUrl(editProduct?.thumbnail_image)
                      }
                      alt="Thumbnail"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border:
                          "1px solid #ddd",
                      }}
                    />

                    <p
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "4px",
                      }}
                    >
                      Product Thumbnail
                    </p>
                  </div>
                )}

              {/* Product Name + Subtitle */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="productName">
                    Product Name
                  </label>

                  <input
                    id="productName"
                    placeholder="Enter product name"
                    value={productName}
                    onChange={(e) =>
                      setProductName(
                        e.target.value
                      )
                    }
                    readOnly={viewMode}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="productSubTitle">
                    Sub Title
                  </label>

                  <input
                    id="productSubTitle"
                    placeholder="Enter sub title"
                    value={productSubTitle}
                    onChange={(e) =>
                      setProductSubTitle(
                        e.target.value
                      )
                    }
                    readOnly={viewMode}
                  />
                </div>
              </div>

              {/* Category */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="productCategory">
                    Category
                  </label>

                  <select
                    id="productCategory"
                    value={productCategory}
                    onChange={(e) =>
                      setProductCategory(
                        e.target.value
                      )
                    }
                    disabled={viewMode}
                  >
                    <option value="">
                      Select category
                    </option>

                    <option value="Electronics">
                      Electronics
                    </option>

                    <option value="Industrial tools">
                      Industrial tools
                    </option>

                    <option value="Medical Equipment">
                      Medical Equipment
                    </option>

                    <option value="Office Supplies">
                      Office Supplies
                    </option>

                    <option value="Construction">
                      Construction
                    </option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="form-group">
                <label htmlFor="productDescription">
                  Product Description
                </label>

                <textarea
                  id="productDescription"
                  placeholder="Enter product description"
                  value={productDescription}
                  onChange={(e) =>
                    setProductDescription(
                      e.target.value
                    )
                  }
                  readOnly={viewMode}
                />
              </div>

              {/* Specifications */}
              <div>
                <label>Specifications</label>

                {specs.map((spec, index) => (
                  <div
                    key={index}
                    className="form-row"
                    style={{
                      marginTop: "10px",
                    }}
                  >
                    <input
                      placeholder="Key"
                      value={spec.key}
                      onChange={(e) =>
                        handleSpecChange(
                          index,
                          "key",
                          e.target.value
                        )
                      }
                      readOnly={viewMode}
                    />

                    <input
                      placeholder="Value"
                      value={spec.value}
                      onChange={(e) =>
                        handleSpecChange(
                          index,
                          "value",
                          e.target.value
                        )
                      }
                      readOnly={viewMode}
                    />

                    {!viewMode && (
                      <button
                        type="button"
                        onClick={() =>
                          removeSpecification(
                            index
                          )
                        }
                        className="small-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}

                {!viewMode && (
                  <p
                    className="add-link"
                    onClick={
                      addSpecification
                    }
                  >
                    + Add Specification
                  </p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label>Tags</label>

                {!viewMode && (
                  <div className="tag-row">
                    <input
                      placeholder="Add Tag"
                      value={tagInput}
                      onChange={(e) =>
                        setTagInput(
                          e.target.value
                        )
                      }
                    />

                    <Button
                      type="button"
                      className="CommonButton"
                      onClick={addTag}
                    >
                      Add
                    </Button>
                  </div>
                )}

                <div className="tags">
                  {tags.map((tag, i) => (
                    <span
                      key={i}
                      style={{
                        marginRight: "8px",
                      }}
                    >
                      {tag}

                      {!viewMode && (
                        <button
                          type="button"
                          onClick={() =>
                            removeTag(i)
                          }
                          style={{
                            marginLeft: "5px",
                            cursor: "pointer",
                            border: "none",
                            background:
                              "transparent",
                          }}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>

              {/* Industries */}
              <div>
                <label>Industries</label>

                <select
                  multiple
                  className="multi-select"
                  value={productIndustries}
                  onChange={(e) => {
                    const values =
                      Array.from(
                        e.target
                          .selectedOptions,
                        (option) =>
                          option.value
                      );

                    setProductIndustries(
                      values
                    );
                  }}
                  disabled={viewMode}
                >
                  <option value="Manufacturing">
                    Manufacturing
                  </option>

                  <option value="Healthcare">
                    Healthcare
                  </option>

                  <option value="Construction">
                    Construction
                  </option>

                  <option value="Technology">
                    Technology
                  </option>
                </select>
              </div>

              {/* Uploads */}
              <div className="upload-grid">
                {uploadConfig.map(
                  (config) => (
                    <div
                      key={config.type}
                      style={{
                        display: "flex",
                        flexDirection:
                          "column",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems:
                            "center",
                          gap: "8px",
                        }}
                      >
                        <p
                          style={{
                            fontWeight:
                              "500",
                            margin: 0,
                          }}
                        >
                          {config.title}
                        </p>

                        {selectedFiles[
                          config.type
                        ] && (
                            <CheckCircle
                              size={18}
                              color="#4CAF50"
                            />
                          )}
                      </div>

                      <div
                        style={{
                          border:
                            selectedFiles[
                              config.type
                            ]
                              ? "2px solid #4CAF50"
                              : "1px dashed #ccc",
                          padding: "10px",
                          borderRadius:
                            "16px",
                          display: "flex",
                          flexDirection:
                            "column",
                          justifyContent:
                            "center",
                          alignItems:
                            "center",
                          gap: "10px",
                        }}
                      >
                        {!selectedFiles[
                          config.type
                        ] ? (
                          <>
                            <Upload
                              size={25}
                              style={{
                                cursor:
                                  "pointer",
                                color:
                                  "#5C308D",
                              }}
                              onClick={() =>
                                openFilePicker(
                                  config.type
                                )
                              }
                            />

                            <div
                              style={{
                                textAlign:
                                  "center",
                              }}
                            >
                              <p>
                                Click to
                                upload
                              </p>

                              <p
                                style={{
                                  color:
                                    "#999",
                                }}
                              >
                                {
                                  config.accept
                                }
                              </p>
                            </div>
                          </>
                        ) : (
                          <div
                            style={{
                              textAlign:
                                "center",
                            }}
                          >
                            <p
                              style={{
                                color:
                                  "#4CAF50",
                                fontWeight:
                                  "600",
                              }}
                            >
                              ✓{" "}
                              {
                                selectedFiles[
                                  config
                                    .type
                                ].name
                              }
                            </p>

                            <p
                              style={{
                                fontSize:
                                  "12px",
                              }}
                            >
                              {(
                                selectedFiles[
                                  config
                                    .type
                                ].size /
                                1024
                              ).toFixed(
                                2
                              )}{" "}
                              KB
                            </p>

                            <div
                              style={{
                                display:
                                  "flex",
                                gap: "10px",
                                justifyContent:
                                  "center",
                                marginTop:
                                  "10px",
                              }}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  openFilePicker(
                                    config.type
                                  )
                                }
                                className="small-btn"
                              >
                                Replace
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  clearFile(
                                    config.type
                                  )
                                }
                                className="small-btn"
                              >
                                <Trash2
                                  size={
                                    14
                                  }
                                />{" "}
                                Remove
                              </button>
                            </div>
                          </div>
                        )}

                        <input
                          ref={(el) =>
                          (fileInputRefs.current[
                            config.type
                          ] = el)
                          }
                          type="file"
                          accept={
                            config.accept
                          }
                          onChange={(e) =>
                            handleFileChange(
                              e,
                              config.type
                            )
                          }
                          style={{
                            display:
                              "none",
                          }}
                        />
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-cancel"
                onClick={onClose}
              >
                {viewMode
                  ? "Close"
                  : "Cancel"}
              </button>

              {!viewMode && (
                <Button
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Saving..."
                    : editProduct
                      ? "Update Product"
                      : "Create Product"}
                </Button>
              )}
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default ProductModal;